import type { ReactNode, Ref } from 'react'
import { XIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export type DismissibleComplianceMarkShellProps = {
  hostRef: Ref<HTMLSpanElement>
  dismissLabel: string
  onDismiss: () => void
  children: ReactNode
  variant: 'blue' | 'yellow'
  /** When true, the shell spans the full editor width instead of shrinking to content. */
  fullWidth?: boolean
}

export function DismissibleComplianceMarkShellDismissButton({
  dismissLabel,
  onDismiss,
  variant,
}: {
  dismissLabel: string
  onDismiss: () => void
  variant: 'blue' | 'yellow'
}) {
  return (
    <button
      type='button'
      aria-label={dismissLabel}
      className={cn(
        'h-auto shrink-0 text-xs font-normal underline-offset-2 hover:underline',
        {
          'text-blue-800 hover:text-blue-900 dark:text-blue-100 dark:hover:text-blue-100':
            variant === 'blue',
          'text-yellow-800 hover:text-yellow-900 dark:text-yellow-100 dark:hover:text-yellow-100':
            variant === 'yellow',
        }
      )}
      onMouseDown={(event) => {
        event.preventDefault()
      }}
      onClick={onDismiss}
    >
      <XIcon className='size-4' />
    </button>
  )
}

/**
 * Shared layout for compliance placeholder marks: ProseMirror contentDOM is the last child
 * (visually hidden with pointer-events none so the left edge cannot receive a caret).
 * Dismiss uses the same Button and spacing as insufficient-information.
 */
export function DismissibleComplianceMarkShell({
  hostRef,
  dismissLabel,
  onDismiss,
  children,
  variant,
  fullWidth = false,
}: DismissibleComplianceMarkShellProps) {
  return (
    <span
      ref={hostRef}
      contentEditable={false}
      className={cn(
        'cursor-default items-stretch gap-1 rounded border-l-4 px-2 py-1 select-none **:select-none *:last:pointer-events-none *:last:sr-only',
        fullWidth
          ? 'flex w-full'
          : 'inline-flex max-w-full align-baseline',
        {
          'border-blue-500 bg-blue-50 dark:bg-blue-950': variant === 'blue',
          'border-yellow-500 bg-yellow-50 dark:bg-yellow-950':
            variant === 'yellow',
        }
      )}
    >
      <span
        className={cn(
          'flex min-w-0 flex-col gap-1 pb-0.5',
          fullWidth ? 'w-full' : 'max-w-full'
        )}
      >
        {children}
        <div className='flex w-full justify-end'>
          <DismissibleComplianceMarkShellDismissButton
            dismissLabel={dismissLabel}
            onDismiss={onDismiss}
            variant={variant}
          />
        </div>
      </span>
    </span>
  )
}
